import type { MergeStep } from "./ooxml-merge";

export const REDLINE_FAILURE_STAGES = [
    "range_unresolved",
    "bookmark_range_null",
    "invalid_bookmark_name",
    "untracked_write_detected",
    "merge_failed",
    "backend_repair_exhausted",
    "agent_recovery_threw",
    "agent_recovery_drifted",
    "execute_js_failed",
] as const;

export type RedlineFailureStage = (typeof REDLINE_FAILURE_STAGES)[number];

export const SNAPSHOT_STATUSES = [
    "stored",
    "too_large",
    "capture_failed",
    "upload_failed",
    "not_captured",
] as const;

export type SnapshotStatus = (typeof SNAPSHOT_STATUSES)[number];

export interface RedlineFailureReport {
    snapshotId: string | null;
    snapshotStatus: SnapshotStatus;
}

export interface WordErrorFields {
    code: string | null;
    message: string | null;
    location: string | null;
}

export interface AttemptedOoxmlPair {
    attemptedOoxml: string;
    currentRangeOoxml: string;
}

export interface RecoveryDrift {
    distance: number;
    reason: string;
    expectedText: string;
    actualText: string;
    divergenceOffset: number | null;
}

export type RedlineFailure =
    | {
        stage: "range_unresolved";
        bookmarks: string[];
        attemptedOoxml: string;
    }
    | {
        stage: "bookmark_range_null";
        bookmarks: string[];
        attemptedOoxml: string;
    }
    | {
        stage: "invalid_bookmark_name";
        bookmarks: string[];
        invalidBookmarks: string[];
        attemptedOoxml: string;
    }
    | {
        stage: "untracked_write_detected";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        lostParagraphs: number;
        addedParagraphs: number;
    }
    | {
        stage: "merge_failed";
        mergeStep: MergeStep;
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        wordError: WordErrorFields | null;
    }
    | {
        stage: "backend_repair_exhausted";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        wordError: WordErrorFields | null;
        repairAttempts: number;
        repairFailureMessage: string;
    }
    | {
        stage: "agent_recovery_threw";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        wordError: WordErrorFields | null;
        agentFailureMessage: string;
    }
    | {
        stage: "agent_recovery_drifted";
        bookmarks: string[];
        ooxml: AttemptedOoxmlPair;
        wordError: WordErrorFields | null;
        drift: RecoveryDrift;
    }
    | {
        stage: "execute_js_failed";
        script: string;
        stdout: string;
        scriptError: string;
    };

export function readMergeStep(failure: RedlineFailure): MergeStep | null {
    return failure.stage === "merge_failed" ? failure.mergeStep : null;
}

export function readWordError(failure: RedlineFailure): WordErrorFields | null {
    switch (failure.stage) {
        case "merge_failed":
        case "backend_repair_exhausted":
        case "agent_recovery_threw":
        case "agent_recovery_drifted":
            return failure.wordError;
        default:
            return null;
    }
}

export function readAttemptedPayload(failure: RedlineFailure): string {
    switch (failure.stage) {
        case "untracked_write_detected":
        case "merge_failed":
        case "backend_repair_exhausted":
        case "agent_recovery_threw":
        case "agent_recovery_drifted":
            return failure.ooxml.attemptedOoxml;
        case "range_unresolved":
        case "bookmark_range_null":
        case "invalid_bookmark_name":
            return failure.attemptedOoxml;
        case "execute_js_failed":
            return failure.script;
    }
}

export function normalizeWordErrorFields(
    wordError: { code?: string; message?: string; location?: string } | undefined,
): WordErrorFields | null {
    if (wordError === undefined) return null;

    return {
        code: wordError.code ?? null,
        message: wordError.message ?? null,
        location: wordError.location ?? null,
    };
}
